import type { ElementType } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Status } from "../../../redux/interfaces/folder.types";

type ResolvedProofOfAddressStatus =
  | "processing"
  | Status.PendingReview
  | Status.Valid;

interface StatusPresentation {
  readonly accessibleLabel: string;
  readonly description: string;
  readonly heading: string;
  readonly icon: ElementType;
  readonly iconClassName: string;
  readonly iconContainerClassName: string;
}

const STATUS_PRESENTATIONS = {
  processing: {
    accessibleLabel: "Comprobante en proceso de validación",
    description:
      "Recibimos tu comprobante de domicilio y estamos validándolo. Esto puede tomar unos momentos.",
    heading: "Validando comprobante",
    icon: Loader2,
    iconClassName: "text-[#28397a] animate-spin",
    iconContainerClassName: "bg-blue-50 ring-blue-50/50",
  },
  [Status.PendingReview]: {
    accessibleLabel: "Comprobante recibido",
    description:
      "Tu comprobante está pendiente de revisión interna. Te avisaremos cuando termine la revisión.",
    heading: "Comprobante recibido",
    icon: CheckCircle2,
    iconClassName: "text-emerald-600",
    iconContainerClassName: "bg-emerald-50 ring-emerald-50/50",
  },
  [Status.Valid]: {
    accessibleLabel: "Comprobante verificado",
    description:
      "Tu comprobante de domicilio ha sido verificado. Puedes continuar con el siguiente paso.",
    heading: "Comprobante aprobado",
    icon: CheckCircle2,
    iconClassName: "text-emerald-600",
    iconContainerClassName: "bg-emerald-50 ring-emerald-50/50",
  },
} satisfies Record<ResolvedProofOfAddressStatus, StatusPresentation>;

interface ProofOfAddressStatusProps {
  readonly status: ResolvedProofOfAddressStatus;
  readonly canContinue?: boolean;
}

export function ProofOfAddressStatus({
  status,
  canContinue,
}: ProofOfAddressStatusProps) {
  const presentation = STATUS_PRESENTATIONS[status];
  const Icon = presentation.icon;

  return (
    <div className="w-full flex items-center justify-center min-h-[60vh]">
      <div
        role="status"
        aria-label={presentation.accessibleLabel}
        className="flex flex-col items-center justify-center px-6 max-w-md w-full"
      >
        <div
          className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ring-8 ${presentation.iconContainerClassName}`}
        >
          <Icon className={`w-10 h-10 ${presentation.iconClassName}`} />
        </div>

        <h3 className="text-xl font-semibold text-slate-900 mb-2 text-center">
          {presentation.heading}
        </h3>
        <p className="text-sm text-slate-600 mb-8 text-center leading-relaxed">
          {status === Status.PendingReview && canContinue === true
            ? "Tu comprobante está pendiente de revisión interna. Puedes continuar con el siguiente paso."
            : presentation.description}
        </p>
      </div>
    </div>
  );
}
